import { AppLanguage, getLanguageStrings } from "internationalisation";
import { RespondentConfigBuilder } from "../respondentConfigBuilder";
import { assertNever } from "../helper";
import { WebPlatform } from "../types";
import { generateExternalSurveyTask, generateUploadsTask, generateWebPlatformTask } from "../tasks";

export const CINNE0226CintCollGroupIds = [
	"CINE-463-0226-CA-YAHOO",
	"CINE-464-0226-CA-FACEBOOK",
] as const;

export const CINE0226AmplifiedCollGroupIds = [
	"CINE-462-0226-CA-YAHOO-RETARGETING",
] as const;

export const CINE0226CollGroupdIds = [
	...CINNE0226CintCollGroupIds,
	...CINE0226AmplifiedCollGroupIds,
] as const;

export type CINE0226CintCollGroupId = typeof CINNE0226CintCollGroupIds[number];
export type CINE0226AmplifiedCollGroupId = typeof CINE0226AmplifiedCollGroupIds[number];
export type CINE0226CollGroupId = typeof CINE0226CollGroupdIds[number];

type CINE0226CollConfig = {
	groupId: CINE0226CollGroupId;
	metaFieldNames: ReadonlyArray<string>;
}

export const CINE0226WebPlatformIds = [
	"CINE-0226-CA-YAHOO-RETARGETING",
	"CINE-0226-CA-YAHOO-BASELINE",
	"CINE-0226-CA-FACEBOOK",
] as const;

export type CINE0226WebPlatformId = typeof CINE0226WebPlatformIds[number];

const getWPTaskLengthMinutes = (webPlatformId: CINE0226WebPlatformId): string => {
	switch (webPlatformId) {
		case "CINE-0226-CA-YAHOO-RETARGETING":
			return "5";
		case "CINE-0226-CA-YAHOO-BASELINE":
			return "5";
		case "CINE-0226-CA-FACEBOOK":
			return "7";
		default:
			assertNever(webPlatformId);
	}
}

const getWebPlatformId = (groupId: CINE0226CollGroupId): CINE0226WebPlatformId => { 
	switch (groupId) {
		case "CINE-462-0226-CA-YAHOO-RETARGETING":
			return "CINE-0226-CA-YAHOO-RETARGETING";
		case "CINE-463-0226-CA-YAHOO":
			return "CINE-0226-CA-YAHOO-BASELINE";
		case "CINE-464-0226-CA-FACEBOOK":
			return "CINE-0226-CA-FACEBOOK";
		default:
			assertNever(groupId);
	}
}

const getWebPlatfromConfigUrl = (webPlatformId: CINE0226WebPlatformId): string => {
	return `https://atrace-cdn.amplifiedintelligence.io/WebPlatform/Configs/CINE-0226/${webPlatformId}.json`
}

const getWebPlatform = (webPlatformId: CINE0226WebPlatformId): WebPlatform => {
	switch (webPlatformId) {
		case "CINE-0226-CA-YAHOO-RETARGETING":
		case "CINE-0226-CA-YAHOO-BASELINE":
			return "Yahoo";
		case "CINE-0226-CA-FACEBOOK":
			return "Facebook";
		default:
			assertNever(webPlatformId);
	}
}

const getSurveyUrl = (groupId: CINE0226CollGroupId): string => {
	switch (groupId) {
		case "CINE-462-0226-CA-YAHOO-RETARGETING":
			return "https://survey.au1.qualtrics.com/jfe/form/SV_agZ4swaBUQkBUrk";
		case "CINE-463-0226-CA-YAHOO":
			return "https://survey.au1.qualtrics.com/jfe/form/SV_8x2KIG8sbz6FTGm";
		case "CINE-464-0226-CA-FACEBOOK":
			return "https://survey.au1.qualtrics.com/jfe/form/SV_2gbB4feGGRkDwWy";
		default:
			assertNever(groupId);
	}
}

export const addCINE0226CollConfig = (
	config: CINE0226CollConfig,
	builder: RespondentConfigBuilder
): RespondentConfigBuilder => {
	const FINISH_MESSAGE = "🎉 Nice job, you’re all set! Your browsing session is complete and your data has been uploaded. Thanks for your time - your reward is on its way!";

	const langStrings = getLanguageStrings(AppLanguage.en);

	const webPlatformId = getWebPlatformId(config.groupId);
	
	const webPlatformTask = generateWebPlatformTask({
		groupId: config.groupId,
		platform: getWebPlatform(webPlatformId),
		webPlatformConfigUrl: getWebPlatfromConfigUrl(webPlatformId),
		webPlatformID: webPlatformId,
		taskLengthMinutes: getWPTaskLengthMinutes(webPlatformId),
		useWebPlatformIDForTaskID: true,
		langStrings: getLanguageStrings(AppLanguage.en),
	});

	const externalSurveyTask = generateExternalSurveyTask({
		title: langStrings.Common.survey,
		description: langStrings.Survey.description,
		extraMetaFields: config.metaFieldNames,
		taskID: `${config.groupId}-SURVEY`,
		surveyUrl: getSurveyUrl(config.groupId),
	});

	const uploadesTask = generateUploadsTask({
		config: {
			title: "Uploads"
		},
		taskId: `${config.groupId}-UPLOADS`,
	});

	builder.setFlag("deviceLock", "mobile");
	builder.setFlag("requireMinimumBattery", 0.3);
	builder.setFlag("finishMessageOverride", FINISH_MESSAGE);
	builder.setFlag("preventTaskProgress", false);
	builder.addTask(webPlatformTask);
	builder.addTask(externalSurveyTask);
	builder.addTask(uploadesTask);

	return builder;
}